import React, { Suspense, useState, useMemo, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import BaseLayout from './BaseLayout';
import TopNav from '../core/components/TopNav';
import NavBar from '../core/components/NavBar';
import { Paper, useMediaQuery } from '@mui/material';
import { useSidebar } from '../context/SideBarContext';
import { GoogleMapsProvider } from '../context/GoogleMapContext';
import { UtilProvider } from '../context/utilContext';

const DefaultLayout: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [isOpen, setOpen] = useState(false);
  const [languageOn, setLanguageOn] = useState(false);
  const { setSideBarOpen, setSideBarWidth } = useSidebar();
  const isSmallScreen = useMediaQuery('(max-width:850px)');
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);
  const location = useLocation();

  // Dynamically adjust sidebar width
  const sidebarWidth = isSmallScreen ? 56 : isOpen ? 300 : 96; // Store as a number for calculations

  // Update context values
  useEffect(() => {
    setSideBarWidth(sidebarWidth);
    setSideBarOpen(isOpen);
  }, [sidebarWidth, isOpen, setSideBarOpen, setSideBarWidth]);

  useEffect(() => {
    const handleResize = () => {
      setWindowWidth(window.innerWidth);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Calculate Paper width
  // const contentWidth = useMemo(() => window.innerWidth - sidebarWidth, [sidebarWidth]);
  const contentWidth = useMemo(() => windowWidth - sidebarWidth, [windowWidth, sidebarWidth]);

  // Check if the current page is /notifications
  const isNotificationsPage = location.pathname === '/notifications';

  useEffect(() => {
    const interval = setInterval(() => {
      const skipElement = document.querySelector('.skiptranslate') as HTMLElement | null;
      if (skipElement && getComputedStyle(skipElement).display !== 'none') {
        setLanguageOn(true);
      } else {
        setLanguageOn(false);
      }
    }, 1000); // check every 1s

    return () => clearInterval(interval);
  }, []);

  return (
    <BaseLayout>
      <div className="h-screen flex">
        {/* Left Sidebar (Fixed) */}
        <div
          className="fixed left-0 top-0 h-screen transition-all duration-500 ease-in-out"
          style={{ width: sidebarWidth }}
        >
          <NavBar opened={setOpen} />
        </div>

        {/* Right section (Main Content) */}
        <div
          className="flex flex-col flex-1 transition-all duration-500 ease-in-out"
          style={{ marginLeft: sidebarWidth }}
        >
          {/* Top Navigation (Fixed) */}
          <div
            className={`${
              languageOn ? 'top-[39px]' : 'top-0'
            } fixed right-0 h-[64px] z-[999] transition-all duration-500 ease-in-out`}
            style={{ left: sidebarWidth }}
          >
            <TopNav />
          </div>

          {/* Scrollable Content */}
          <Paper
            className={`mt-[64px] p-[24px] h-screen overflow-x-hidden transition-all duration-500 ease-in-out`}
            style={{ width: `${contentWidth}px`, backgroundColor: '#fcfcfc' }}
          >
            <GoogleMapsProvider>
              <UtilProvider>
                <Suspense>{children}</Suspense>
              </UtilProvider>
            </GoogleMapsProvider>
          </Paper>
        </div>
      </div>
    </BaseLayout>
  );
};

export default DefaultLayout;
